/** Whether a WGS84 click (lng, lat) lies on / inside a GeoJSON geometry (identify at point). */

const EARTH_RADIUS_M = 6_371_000;

export interface GeoJsonContainsPointOptions {
  /** Max distance to LineString / MultiLineString in metres (map identify tolerance). */
  lineToleranceM?: number;
}

const DEFAULT_LINE_TOLERANCE_M = 12;

export function geoJsonContainsPoint(
  geometry: unknown,
  lng: number,
  lat: number,
  options?: GeoJsonContainsPointOptions,
): boolean {
  if (!geometry || typeof geometry !== 'object' || !Number.isFinite(lng) || !Number.isFinite(lat)) {
    return false;
  }
  const lineTol = options?.lineToleranceM ?? DEFAULT_LINE_TOLERANCE_M;
  const rec = geometry as Record<string, unknown>;
  const type = String(rec['type'] ?? '');
  const coords = rec['coordinates'];
  switch (type) {
    case 'Point':
      return pointEqualsCoord(coords, lng, lat, lineTol);
    case 'MultiPoint':
      return Array.isArray(coords) && (coords as unknown[]).some((c) => pointEqualsCoord(c, lng, lat, lineTol));
    case 'LineString':
      return lineStringContainsPoint(coords, lng, lat, lineTol);
    case 'MultiLineString':
      return (
        Array.isArray(coords)
        && (coords as unknown[]).some((ring) => lineStringContainsPoint(ring, lng, lat, lineTol))
      );
    case 'Polygon':
      return polygonContainsPoint(coords, lng, lat);
    case 'MultiPolygon':
      return (
        Array.isArray(coords)
        && (coords as unknown[]).some((poly) => polygonContainsPoint(poly, lng, lat))
      );
    case 'GeometryCollection': {
      const geometries = rec['geometries'];
      return (
        Array.isArray(geometries)
        && (geometries as unknown[]).some((g) => geoJsonContainsPoint(g, lng, lat, options))
      );
    }
    default:
      return false;
  }
}

function polygonContainsPoint(coords: unknown, lng: number, lat: number): boolean {
  if (!Array.isArray(coords) || !coords.length) return false;
  const rings = coords as unknown[];
  const exterior = ringLngLat(rings[0]);
  if (!exterior.length || !pointInRing(lng, lat, exterior)) return false;
  for (let h = 1; h < rings.length; h += 1) {
    const hole = ringLngLat(rings[h]);
    if (hole.length && pointInRing(lng, lat, hole)) return false;
  }
  return true;
}

function ringLngLat(raw: unknown): Array<[number, number]> {
  if (!Array.isArray(raw)) return [];
  const out: Array<[number, number]> = [];
  for (const pair of raw) {
    const lngLat = coordLngLat(pair);
    if (lngLat) out.push(lngLat);
  }
  return out;
}

function pointInRing(lng: number, lat: number, ring: Array<[number, number]>): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (((yi > lat) !== (yj > lat)) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function lineStringContainsPoint(coords: unknown, lng: number, lat: number, toleranceM: number): boolean {
  const ring = ringLngLat(coords);
  if (ring.length < 2) return ring.length === 1 && pointInRing(lng, lat, ring);
  for (let i = 0; i < ring.length - 1; i += 1) {
    if (pointToSegmentDistanceM(lng, lat, ring[i], ring[i + 1]) <= toleranceM) return true;
  }
  return false;
}

function pointEqualsCoord(raw: unknown, lng: number, lat: number, toleranceM: number): boolean {
  const c = coordLngLat(raw);
  if (!c) return false;
  return haversineM(lng, lat, c[0], c[1]) <= toleranceM;
}

function coordLngLat(raw: unknown): [number, number] | null {
  if (!Array.isArray(raw) || raw.length < 2) return null;
  const lng = Number(raw[0]);
  const lat = Number(raw[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return [lng, lat];
}

function pointToSegmentDistanceM(
  px: number,
  py: number,
  a: [number, number],
  b: [number, number],
): number {
  const [ax, ay] = a;
  const [bx, by] = b;
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) return haversineM(px, py, ax, ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
  const qx = ax + t * dx;
  const qy = ay + t * dy;
  return haversineM(px, py, qx, qy);
}

function haversineM(lng1: number, lat1: number, lng2: number, lat2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}
